<template>
    <div class="checkout booking-change">
        <div v-if="summary">
            <!-- Summary -->
            <div class="summary">
                <h3 class="text-center">
                    {{ 'checkout.summary.title' | trans }}
                </h3>

                <!-- Travel period -->
                <div class="travel-period">
                    <div class="row">
                        <div class="col">
                            <span class="day">
                                {{ summary.arrive_date.format('DD') }}.
                            </span>
                            <span class="month">
                                {{ summary.arrive_date.format('MMM') }}
                                {{ summary.arrive_date.format('YYYY') }}
                            </span>
                            <small class="weekday">
                                {{ summary.arrive_date.format('dd') }}.
                            </small>
                        </div>

                        <div class="col">
                            <span class="day">
                                {{ summary.departure_date.format('DD') }}.
                            </span>
                            <span class="month">
                                {{ summary.departure_date.format('MMM') }}
                                {{ summary.departure_date.format('YYYY') }}
                            </span>
                            <small class="weekday">
                                {{ summary.departure_date.format('dd') }}.
                            </small>
                        </div>
                    </div>
                </div>

                <!-- Activities -->
                <div class="activity" v-for="activity in summary.activities">
                    <div class="divider"></div>

                    <div class="ticket" v-for="ticket in activity.tickets">
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <span class="amount">
                                            {{ ticket.amount }}x
                                        </span>
                                    </td>
                                    <td>
                                        <strong class="name">
                                            {{ ticket.name }}
                                        </strong>
                                        <small class="subtitle">
                                            {{ ticket.subtitle }}
                                        </small>

                                        <div class="row date-time">
                                            <div class="col" v-if="ticket.date">
                                                <label>
                                                    {{ 'checkout.summary.ticketDate' | trans }}
                                                </label>
                                                {{ $moment(ticket.date, 'YYYY-MM-DD').format('dddd') }}, {{ $moment(ticket.date, 'YYYY-MM-DD').format('DD. MMMM YYYY') }}
                                            </div>
                                            <div class="col" v-if="ticket.time">
                                                <label>
                                                    {{ 'checkout.summary.ticketTime' | trans }}
                                                </label>
                                                {{ $moment(ticket.time, 'HH:mm:ss').format('HH:mm') }}
                                                {{ 'checkout.summary.clock' | trans }}
                                            </div>
                                            <div class="col" v-if="ticket.validity && ticket.time === null">
                                                <label>
                                                    {{ 'checkout.summary.validity' | trans }}
                                                </label>
                                                {{ ticket.validity }}
                                            </div>
                                        </div>

                                        <span class="age-group">
                                            {{ ticket.age_group }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Rooms (Accommodations) -->
                <div class="divider" v-if="summary.rooms.length > 0"></div>
                <div class="accommodation" v-for="room in summary.rooms">
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <strong class="name">
                                        {{ room.accommodation.name }}

                                        <span class="stars" v-if="room.stars > 0">
                                            <i class="fa fa-star" v-for="i in room.stars / 1"></i>
                                            <i class="fa fa-star-half" v-if="room.stars%1 > 0"></i>
                                        </span>
                                    </strong>
                                    <small class="subtitle">
                                        {{ room.address }}
                                    </small>

                                    <div class="row room">
                                        <div class="col">
                                            <label>
                                                {{ 'checkout.summary.room' | trans }}
                                            </label>
                                            {{ room.name }}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="divider"></div>

                <!-- Subtotals -->
                <div class="subtotals" v-if="'total' in summary && (summary.rooms.length > 0 || summary.activities.length > 0 || summary.products.length > 0)">
                    <div v-for="subtotal in summary.subtotals" v-if="'subtotals' in summary">
                        <span class="price">
                            {{ subtotal.subtotal | formatPrice }}
                            &euro;
                        </span>

                        {{ 'checkout.summary.' + subtotal.type | trans }}

                        <span v-if="subtotal.type === 'child'">
                            ({{ subtotal.age }} {{ 'checkout.summary.years' | choice(subtotal.age) }})
                        </span>
                    </div>

                    <!-- Insurance -->
                    <div v-if="summary.insurance">
                        <span class="price">
                            {{ summary.insurance.price | formatPrice }}
                            &euro;
                        </span>

                        {{ summary.insurance.name }}
                    </div>

                    <!-- Shipping -->
                    <div v-if="summary.shipping">
                        <span class="price">
                            {{ summary.shipping.price | formatPrice }}
                            &euro;
                        </span>

                        {{ summary.shipping.name }}
                    </div>

                    <!-- Shipping fee -->
                    <div v-if="summary.shipping_fee">
                        <span class="price">
                            {{ summary.shipping_fee.price | formatPrice }}
                            &euro;
                        </span>

                        {{ summary.shipping_fee.name }}
                    </div>

                    <!-- Discount -->
                    <div v-if="summary.discount">
                        <span class="price">
                            {{ summary.discount.discount | formatPrice }}
                            &euro;
                        </span>

                        Gutscheincode:
                        {{ summary.discount.code }}
                    </div>

                    <!-- Booking fees -->
                    <div v-if="summary.rooms.length > 0 || summary.activities.length > 0">
                        <span class="price">
                            0
                            &euro;
                        </span>

                        {{ 'checkout.summary.bookingFees' | trans }}
                    </div>
                </div>

                <!-- Total -->
                <div class="total">
                    <span class="price">
                        {{ summary.total | formatPrice }}
                        &euro;
                    </span>

                    {{ 'checkout.summary.total' | trans }}
                </div>
            </div>

            <p class="text-center mt-3">
                Bitte klicke auf <strong>Buchung bestätigen</strong>, um die Umbuchung abzuschließen.

                <span class="d-block" v-if="summary.total > 0">
                    Du wirst anschließend zur Bezahlung weitergeleitet.
                </span>
            </p>

            <button type="submit" class="btn btn-primary btn-lg btn-block mt-3" @click="changeBooking()">
                <i class="wa-icon-long-arrow-right float-right ml-2 mt-1"></i>

                Buchung bestätigen
            </button>
        </div>

        <div class="text-center mb-3" v-if="isLoading">
            <svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                <circle class="external-circle" cx="60" cy="60" r="50"></circle>
            </svg>
        </div>
    </div>
</template>

<script>
    export default {
        props: ['inputs', 'booking', 'id'],
        data() {
            return {
                summary: null,
                isLoading: false,
            }
        },
        mounted() {
            this.loadSummary();
        },
        computed: {},
        methods: {
            loadSummary: function () {
                var self = this;
                this.isLoading = true

                // Modify inputs
                var _inputs = Object.assign({}, this.inputs)
                _inputs.insurance = 'no'

                axios.post(route('api.checkout.changeSummary'), _inputs).then(function (response) {
                    var data = response.data.data

                    // Convert dates
                    data.arrive_date = self.$moment(data.arrive_date, 'YYYY-MM-DD');
                    data.departure_date = self.$moment(data.departure_date, 'YYYY-MM-DD');

                    self.summary = data
                    self.isLoading = false
                }).catch(function (error) {
                    console.log(error);
                    self.isLoading = false
                });
            },

            changeBooking() {
                var self = this;
                this.isLoading = true

                // Modify inputs
                var _inputs = Object.assign({}, this.inputs)
                _inputs.insurance = 'no'
                _inputs.booking_change_id = this.id

                axios.post(route('api.checkout.changeBooking'), _inputs).then(function (response) {
                    // Redirect the user
                    window.location.href = response.data.redirect_url

                    self.isLoading = false
                }).catch(function (error) {
                    console.log(error);
                    self.isLoading = false
                });
            }
        },
        watch: {}
    }
</script>
